import React, { useCallback, useEffect, useState } from 'react';
import axiosClient from '@/utils/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Edit as EditIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as zod from 'zod';

import type { Folder } from '@/types/folder';
import ProfessionalDialog from '@/components/common/professional-dialog';

interface EditFolderDialogProps {
  folder: Folder | null;
  onClose: () => void;
  onEdit: () => void;
  open: boolean;
  onSuccess?: () => void;
}

interface FolderApiResults {
  error?: string;
  message: string;
  folder: {
    id: string;
    folder_name: string;
  };
}

const schema = zod.object({
  folder_name: zod.string().nonempty({ message: 'Folder name is required' }),
  visibility: zod.enum(['public', 'private']).default('public'),
});

type Values = zod.infer<typeof schema>;

export function EditFolderDialog({
  folder,
  open,
  onClose,
  onEdit,
  onSuccess,
}: EditFolderDialogProps): React.JSX.Element {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const defaultValues = {
    folder_name: '',
    visibility: 'public' as const,
  } satisfies Values;

  const editFolderRequest = useMutation({
    mutationFn: async (values: Values): Promise<FolderApiResults> => {
      try {
        if (!folder?.id) {
          throw new Error('No folder selected for editing');
        }

        const response = await axiosClient.patch(`/api/v1/folders/${folder.id}`, {
          name: values.folder_name,
          visibility: values.visibility,
        });

        return response.data;
      } catch (error) {
        if (error instanceof AxiosError && error.status === 403) {
          throw new Error('You are not authorized to edit this folder');
        }
        console.log('Error', error);
        throw new Error('An error occurred while editing the folder');
      }
    },
  });

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Values>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  // Set initial form values when folder data changes
  useEffect(() => {
    if (folder) {
      console.log('📁 Setting initial form values:', {
        folderName: folder.name,
        folderVisibility: folder.visibility,
        defaultVisibility: folder.visibility || 'public',
      });
      setValue('folder_name', folder.name);
      setValue('visibility', folder.visibility || 'public');
    }
  }, [folder, setValue]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleEdit = useCallback(
    async (values: Values) => {
      console.log('🔄 Edit folder form submitted:', values);
      await axiosClient.get('sanctum/csrf-cookie');

      try {
        console.log('📡 Making API call to update folder...');
        const response = await editFolderRequest.mutateAsync(values);
        console.log('✅ API response:', response);

        if (response.error) {
          console.error('❌ API returned error:', response.error);
          toast.error(response.error);
          return;
        }

        console.log('🎉 Folder updated successfully, calling onEdit callback');
        toast.success(response.message || 'Folder updated successfully');
        onEdit();
        reset();
      } catch (error) {
        console.error('🚨 Error updating folder:', error);
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unknown error occurred');
        }
      }
    },
    [editFolderRequest, onEdit, reset]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!folder?.id) return;

    try {
      await axiosClient.delete(`/api/v1/folders/${folder.id}`);
      toast.success('Folder deleted successfully');
      onEdit(); // Refresh the parent component
      onClose();
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('🚨 Error deleting folder:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to delete folder');
      }
    }
  }, [folder?.id, onEdit, onClose]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  return (
    <>
      <ProfessionalDialog
        autoHeight
        fullWidth
        icon={<EditIcon />}
        maxWidth="sm"
        onClose={onClose}
        open={open}
        subtitle="Update folder name and visibility settings"
        title="Edit Folder"
        variant="elevated"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            console.log('🔄 Form submitted via form element');
            console.log('📋 Current form values:', watch());
            console.log('❌ Form errors:', errors);
            console.log('🎯 Calling handleSubmit...');
            console.log('🔍 Form validation result:', schema.safeParse(watch()));
            handleSubmit(handleEdit)(e);
          }}
        >
          <Stack spacing={3}>
            <FormControl error={Boolean(errors.folder_name)} fullWidth>
              <InputLabel htmlFor="folder_name">Folder Name</InputLabel>
              <OutlinedInput
                {...register('folder_name')}
                fullWidth
                id="folder_name"
                placeholder="Enter a folder name"
                type="text"
              />
              <FormHelperText error>{errors.folder_name?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('visibility') === 'private'}
                    onChange={(e) => {
                      const newValue = e.target.checked ? 'private' : 'public';
                      console.log('🔄 Setting visibility to:', newValue);
                      setValue('visibility', newValue);
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      {watch('visibility') === 'private' ? 'Private' : 'Public'}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {watch('visibility') === 'private'
                        ? '(Only community managers and account admins can see this folder)'
                        : '(All community members can see this folder)'}
                    </Typography>
                  </Box>
                }
              />
            </FormControl>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 3 }}>
              <Stack direction="row" spacing={2}>
                <LoadingButton onClick={onClose} size="medium" variant="outlined">
                  Cancel
                </LoadingButton>

                {/* Delete Button - Only show for empty folders */}
                {folder && folder.item_count === 0 ? <LoadingButton
                    color="error"
                    disabled={editFolderRequest.isPending}
                    onClick={handleDeleteClick}
                    size="medium"
                    variant="outlined"
                  >
                    Delete Folder
                  </LoadingButton> : null}
              </Stack>

              <LoadingButton
                color="primary"
                loading={editFolderRequest.isPending}
                onClick={() => { console.log('🖱️ Save Changes button clicked'); }}
                size="medium"
                type="submit"
                variant="contained"
              >
                Save Changes
              </LoadingButton>
            </Box>
          </Stack>
        </form>
      </ProfessionalDialog>

      {/* Delete Confirmation Dialog */}
      <ProfessionalDialog
        autoHeight
        fullWidth
        icon={<EditIcon />}
        maxWidth="sm"
        onClose={handleDeleteCancel}
        open={deleteConfirmOpen}
        subtitle={`Are you sure you want to delete "${folder?.name}"? This action cannot be undone.`}
        title="Delete Folder"
        variant="elevated"
      >
        <Stack spacing={3}>
          <Typography color="text.secondary" variant="body2">
            This will permanently remove the folder from the system.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <LoadingButton onClick={handleDeleteCancel} size="medium" variant="outlined">
              Cancel
            </LoadingButton>

            <LoadingButton
              color="error"
              loading={editFolderRequest.isPending}
              onClick={handleDeleteConfirm}
              size="medium"
              variant="contained"
            >
              Delete Folder
            </LoadingButton>
          </Box>
        </Stack>
      </ProfessionalDialog>
    </>
  );
}
